import * as React from 'react';

import type { SeniorityLevelDto } from '@/api/catalog-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SENIORITY_LEVELS } from '@/lib/catalog-labels';
import { cn } from '@/lib/utils';
import type { FamilleNode } from '@/routes/supplier/catalog/catalog-referential-model';

// =============================================================================
// catalog-filters.tsx — BARRE HAUTE : recherche + filtres combinables (DBU §3.4 l.2706-2710).
// =============================================================================
// DBU §3.4 : « une barre de recherche et des filtres en haut ». Recherche texte libre sur LIBELLÉ +
// CODE JeKo + mots-clés de mission (l.2706) ; filtres combinables FAMILLE / SOUS-FAMILLE / NIVEAU
// (l.2708) ; bouton RÉINITIALISER (l.2710). Consultation seule : chaque champ est un ÉTAT D'URL
// (RULE_REF_006 : jamais localStorage), la saisie délègue aux callbacks du parent.
//
// WCAG 2.1 AA : chaque contrôle porte un <Label htmlFor> associé (1.3.1 / 4.1.2), champ de recherche
// type="search", <select> natifs (navigation clavier), focus visible homogène (ring). Les familles /
// sous-familles proviennent de l'arborescence dérivée : les <select> restent cohérents avec le menu.

export interface CatalogFiltersProps {
  /** Arborescence familles → sous-familles (alimente les <select> famille / sous-famille). */
  readonly familles: readonly FamilleNode[];
  readonly search: string | undefined;
  readonly famille: string | undefined;
  readonly sousFamille: string | undefined;
  readonly niveau: SeniorityLevelDto | undefined;
  readonly onSearch: (q: string) => void;
  readonly onSelectFamille: (famille: string | undefined) => void;
  readonly onSelectSousFamille: (sousFamille: string | undefined) => void;
  readonly onSelectNiveau: (niveau: SeniorityLevelDto | undefined) => void;
  /** Réinitialise TOUS les filtres (recherche + famille + sous-famille + niveau). */
  readonly onReset: () => void;
  /** `true` si au moins un filtre est actif (active le bouton Réinitialiser). */
  readonly hasActiveFilters: boolean;
}

/** Classe partagée des <select> natifs (tokens sémantiques ; focus visible clavier). */
const SELECT_CLASS = cn(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
);

export function CatalogFilters({
  familles,
  search,
  famille,
  sousFamille,
  niveau,
  onSearch,
  onSelectFamille,
  onSelectSousFamille,
  onSelectNiveau,
  onReset,
  hasActiveFilters,
}: CatalogFiltersProps): React.JSX.Element {
  // Sous-familles disponibles = celles de la famille sélectionnée (sinon aucune : on demande d'abord
  // une famille). Le <select> sous-famille reste désactivé tant qu'aucune famille n'est choisie.
  const sousFamilles = React.useMemo(() => {
    if (famille === undefined) {
      return [];
    }
    return familles.find((node) => node.key === famille)?.sousFamilles ?? [];
  }, [familles, famille]);

  return (
    <form
      role="search"
      aria-label="Recherche et filtres du référentiel des profils"
      className="flex flex-col gap-4"
      onSubmit={(event) => event.preventDefault()}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="catalog-search">Rechercher un profil</Label>
        <Input
          id="catalog-search"
          type="search"
          placeholder="Libellé, code JeKo ou mot-clé de mission"
          value={search ?? ''}
          onChange={(event) => onSearch(event.target.value)}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="catalog-famille">Famille</Label>
          <select
            id="catalog-famille"
            className={SELECT_CLASS}
            value={famille ?? ''}
            onChange={(event) =>
              onSelectFamille(event.target.value === '' ? undefined : event.target.value)
            }
          >
            <option value="">Toutes les familles</option>
            {familles.map((node) => (
              <option key={node.key} value={node.key}>
                {node.key}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="catalog-sous-famille">Sous-famille</Label>
          <select
            id="catalog-sous-famille"
            className={SELECT_CLASS}
            value={sousFamille ?? ''}
            disabled={famille === undefined}
            onChange={(event) =>
              onSelectSousFamille(
                event.target.value === '' ? undefined : event.target.value,
              )
            }
          >
            <option value="">Toutes les sous-familles</option>
            {sousFamilles.map((node) => (
              <option key={node.key} value={node.key}>
                {node.key}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="catalog-niveau">Niveau d'expertise</Label>
          <select
            id="catalog-niveau"
            className={SELECT_CLASS}
            value={niveau ?? ''}
            onChange={(event) =>
              onSelectNiveau(
                event.target.value === ''
                  ? undefined
                  : (event.target.value as SeniorityLevelDto),
              )
            }
          >
            <option value="">Tous les niveaux</option>
            {SENIORITY_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onReset}
          disabled={!hasActiveFilters}
        >
          Réinitialiser les filtres
        </Button>
      </div>
    </form>
  );
}
